import React, { useState, useEffect, useContext } from "react";
import styled from "styled-components";
import Switch from "@mui/material/Switch";
import Request from "common/Request";
import ActionsContext from "common/ActionsContext";
import {
  getApplicationMetricsMock,
  parseApplicationMetrics,
} from "views/Statistics/DPI/ApplicationMetrics/api";
import BoxplotMultiple from "common/BoxplotMultiple";
import LineChartOrdinal from "common/graphs/LineChartOrdinal";
import { safeStr } from "common/api";
import SelectInputWithIcon from "common/SelectInputWithIcon";
import { AppCharts } from "views/Statistics/DPI/ApplicationMetrics/ApplicationMetrics.jsx";
import TimeCharts from "views/Statistics/DPI/ApplicationMetrics/TimeCharts";

const ChartWrapper = styled.div`
  margin: 0 20px;
`;

const WrapperCharts = styled.div`
  margin-top: -30px;

  h4 {
    font-size: 14px !important;
    padding: 0;
  }

  .subscriber-dashboard {
    padding: 0 20px;
  }
`;

const ChartTitle = styled.h4`
  margin-top: 0;
  margin-bottom: 0;
  font-weight: 100;
  font-family: "AllianceNo2-SemiBold";
  font-size: 14px !important;
  color: var(--Text-Primary);
`;

const HeaderWrapper = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 10px;
  position: relative;

  .sw-input.MuiSwitch-root {
    & .Mui-checked {
      color: var(--switch-checked-green);
    }

    & .Mui-checked + .MuiSwitch-track {
      background-color: var(--switch-checked-light-green);
    }
  }

  h4 {
    padding: 0 20px;
    white-space: nowrap;
  }

  @media screen and (max-width: 1400px) {
    flex-direction: column;
    align-content: flex-start;
    align-items: flex-start;
    h4 {
      margin-top: 30px !important;
      margin-bottom: -10px;
    }

    .header-dropdown {
      position: absolute;
      right: 0;
      top: 0;
    }
  }
`;

const HeaderRight = styled.div`
  display: flex;
  align-items: center;
  margin-right: 5px;
  border-bottom: none !important;

  @media screen and (max-width: 1400px) {
    width: 100%;
  }
  @media screen and (max-width: 800px) {
    flex-wrap: wrap;
  }
`;

const SwitchWithLabel = styled.div`
  display: flex;
  align-items: center;
  position: relative;
  margin: 0 10px;
`;

const Label = styled.div`
  margin-right: 10px;
  font-family: AllianceNo2-SemiBold !important;
  font-weight: 100;
  color: var(--Text-Primary);
  width: 100px;
  text-align: right;
`;

const RefreshButton = styled.div`
  cursor: pointer;
  height: 20px;
`;

const SelectWrapper = styled.div`
  width: 250px;
  margin-right: 20px;
`;

const _displayChoices = [
  { value: "updown", label: "Downlink & Uplink Speed per Flow" },
  { value: "downall", label: "Downlink Speed & Time Evolution" },
  { value: "upall", label: "Uplink Speed & Time Evolution" },
];

function getCmdSubscriber(target) {
  if (!target) return "all";
  const { addr, subsId } = target;
  return addr ? addr : `id ${subsId}`;
}

function getCmdSubscribergroup(group) {
  if (!group) return "all";
  return `name '"${safeStr(group).replaceAll("'", "")}"'`;
}

function DPISpeed({ hours, test = false, target, subscriberGroup }) {
  const actions = useContext(ActionsContext);
  const [plotMode, setPlotMode] = useState("boxplot");
  const [request, setRequest] = useState(null);
  const cmdSubscriber = getCmdSubscriber(target);
  const [display, setDisplay] = useState("downall");
  const [showMinMax, setShowMinMax] = useState(false);

  useEffect(() => {
    doLoad();

    return actions.recv("do-load", doLoad);
  }, [hours]);

  function doLoad() {
    const promise = (
      test
        ? Promise.resolve(getApplicationMetricsMock())
        : ifCl.run(
            `show pkteng app-metrics hours ${hours} categories 19 subscriber ${getCmdSubscriber(
              target
            )} policy all subscriber-group ${getCmdSubscribergroup(
              subscriberGroup
            )} percentile`, { timeout: DPI_STATS_TMOUT_MS }
          )
    )
      .then(parseApplicationMetrics)
      .catch((error) => {
        showModalError("Error", error);
      });

    setRequest(promise);
  }

  return (
    <>
      <HeaderWrapper>
        <h4 className="subtitle">Application Speed Metrics</h4>

        <HeaderRight className="header">
          <SelectWrapper className="displaySelect">
            <SelectInputWithIcon
              title="Display"
              name="Display"
              icon="apps"
              selected={display}
              onChange={({ target }) => setDisplay(target.value)}
              options={_displayChoices}
            />
          </SelectWrapper>
          <SwitchWithLabel>
            <Label>
              {plotMode === "boxplot" ? "Line chart view" : "Boxplot view"}
            </Label>
            <Switch
              className="sw-input"
              checked={plotMode !== "boxplot"}
              onChange={() =>
                setPlotMode(plotMode === "boxplot" ? "line" : "boxplot")
              }
            />
          </SwitchWithLabel>
          <SwitchWithLabel>
            <Label>View max+min</Label>{" "}
            <Switch
              className="sw-input"
              checked={showMinMax}
              onChange={() => setShowMinMax(!showMinMax)}
            />
          </SwitchWithLabel>
          <RefreshButton
            className="header-dropdown"
            onClick={() => actions.send("do-load")}
          >
            <a
              data-toggle="cardloading"
              data-loading-effect="pulse"
              title="Refresh"
            >
              <i class="material-icons">refresh</i>
            </a>
          </RefreshButton>
        </HeaderRight>
      </HeaderWrapper>
      <Request during={request}>
        {(result) => {
          return (
            <WrapperCharts>
              <AppCharts
                data={result}
                plotMode={plotMode}
                info={{
                  titleDown: "Downlink speed per flow",
                  titleUp: "Uplink speed per flow",
                }}
                display={display}
                showMinMax={showMinMax}
                className="subscriber-dashboard"
              />
              {display !== "updown" && (
                <TimeCharts
                  data={result}
                  plotMode={plotMode}
                  hours={hours}
                  display={display}
                  showMinMax={showMinMax}
                  target={target}
                  subscriberGroups={subscriberGroup ? [subscriberGroup] : []}
                  className="subscriber-dashboard"
                />
              )}
            </WrapperCharts>
          );
        }}
      </Request>
    </>
  );
}

export default DPISpeed;
